import * as React from 'react';

import { Button, ContentCard, Loading, SearchField } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucidePlus } from 'lucide-react';

import * as accountDetailsState from '../../_state/accountDetails.state';
import * as accountEditingState from '../../_state/accountEditing.state';
import { useTranslations } from '../../i18n/useTranslations';

import { AddressCard } from './AddressCard';
import { AddressDetails } from './AddressDetails';
import { AddressFormDialog } from './AddressFormDialog';

import { useDebouncedAtom } from '~/shared/hooks/useDebouncedAtom';

export const AccountTabAddresses = () => {
  const t = useTranslations();

  const [showAddressDialog, setShowAddressDialog] = React.useState(false);

  const [searchTerm, setSearchTerm] = useDebouncedAtom(
    accountDetailsState.$searchTerm,
    300,
  );
  // Subscribe directly to the query atom to ensure proper updates after refetch
  const { isLoading: isLoadingRemoteAccount } = useAtomValue(
    accountDetailsState.$remoteAccount,
  );
  const filteredAddresses = useAtomValue(
    accountDetailsState.$filteredAddresses,
  );
  const selectedAddress = useAtomValue(accountDetailsState.$selectedAddress);
  const canEditAccount = useAtomValue(accountEditingState.$canEditAccount);
  const resetAddressForm = useSetAtom(accountEditingState.$resetAddressForm);

  const handleAddAddress = () => {
    setShowAddressDialog(true);
    resetAddressForm();
  };

  return (
    <div className="@AccountTabAddresses grid h-full grid-cols-[270px_auto] gap-3 p-5">
      <AddressFormDialog
        isOpen={showAddressDialog}
        onOpenChange={setShowAddressDialog}
      />
      <Loading isLoading={isLoadingRemoteAccount} className="col-span-2">
        <ContentCard className="flex flex-col p-0">
          <div className="flex gap-2 border-b border-neutral/10 p-2">
            <SearchField
              className={'w-full [&_input]:w-full'}
              placeholder={t.accountsView.addresses.search}
              value={searchTerm}
              onChange={setSearchTerm}
            />
            {canEditAccount && (
              <Button
                icon={<LucidePlus />}
                className="px-2.5"
                onClick={handleAddAddress}
              />
            )}
          </div>
          <div className="no-scrollbar flex h-0 grow flex-col gap-0.5 overflow-auto p-2">
            {filteredAddresses?.map((address, index, addresses) => (
              <AddressCard
                key={address.id}
                address={address}
                isNextCardSelected={
                  index < addresses.length - 1 &&
                  selectedAddress?.id === addresses[index + 1].id
                }
              />
            ))}
            {!isLoadingRemoteAccount && filteredAddresses?.length === 0 && (
              <div className="flex items-center justify-center py-5">
                {t.accountsView.addresses.noAddressesFound}
              </div>
            )}
          </div>
        </ContentCard>
        <AddressDetails />
      </Loading>
    </div>
  );
};
